import axios from "axios"

/**
 * @author Pedro Antonio
 * @class UserInvitedService
 */
export default class UserInvitedService {
    /**
     * Creates an instance of UserInvitedService.
     * @param {String} apiUrl
     * @memberof UserInvitedService
     */

    constructor(apiUrl) {
        this.apiUrl = apiUrl
    }

    /**
     * @author Pedro Antonio
     * @param {string} status
     * @param {String} token
     * @returns {Object}
     * @memberof UserInvitedService
     */
    async getInvitedUsers(token, status = "enabled") {
        return axios.get(`${this.apiUrl}/users-invited`,
            {
                params: {
                    status
                },
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }).then(response => response.data)
    }

    /**
     * @author Pedro Antonio  
     * @typedef {Object} invitedUserData
     * @property {string} email Correo del usuario invitado a cambiar estátus
     * @property {number} clientId Id del cliente del usuario administrador
     * @property {Date} lastInteraction Última fecha de interacción (debe mandarse del dalo del cliente)
     * @typedef {Object} returnedUser
     * @property {invitedUserData} data Datos retornados de parte del servidor
     * @property {number} statusCode Código de estado del servidor
     * @property {string} message Mensaje del servidor
     * @property {string} consecutive UUID referencia de la petición
     * @param {String} email Correo del usuario invitado a actualizar
     * @param {Date} lastInteraction Última fecha de interacción del usuario invitado
     * @param {String} token Token de autorización para hacer peticiones
     * @returns {returnedUser}
     * @memberof UserInvitedService 
     */
    async changeStatusInvited(email, token) {
        let lastInteraction = new Date()
        return axios.put(`${this.apiUrl}/change-status-invited`,
            {
                email,
                lastInteraction
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }).then(response => response.data)
    }

    /**
     * @author Pedro Antonio
     * @param {String} email Correo del usuario invitado a actualizar
     * @param {String} fullName Correo completo del usuario invitado a agregar
     * @param {String} token Token de autorización para hacer peticiones
     * @param {Date} lastInteraction Última fecha de interacción
     * @typedef {object} data
     * @property {object} userInvited
     * @property {number} customStatus
     * @typedef {Object} response
     * @property {string} consecutive
     * @property {number} statusCode
     * @property {data} data
     * @returns {response}
     * @memberof UserInvitedService
     */
    async createUserInvited(email, fullName, token) {
        return axios.post(`${this.apiUrl}/create-invited-user`,
            {
                email,
                fullName,
                lastInteraction: new Date()
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }).then(response => response.data)
    }

    /**
    * @author Nestor Sandoval
    * @function resendInvitation
    * @description Resends the onboarding invitation to an invited user who has not completed their registration
    * @param {String} token - Authorization token used to perform the request
    * @param {String} email - Email address of the invited user
    * @returns {Promise<Object>} Server response with the operation status
    * @memberof UserInvitedService
    */
    async resendInvitation(token, email) {
        return axios.post(`${this.apiUrl}/resend-invitation`,
            { email },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }).then(response => response.data)
    }

    /**
   * @author Nestor Sandoval
   * @function updateInvitedUserNoPassword
   * @description Sends a request to update whether an invited user can access the platform without a password.
   * @param {String} token - Authorization token used for the request.
   * @param {String} email - Email of the invited user whose access configuration will be updated.
   * @param {Boolean} noPasswordRequired - Indicates whether the user should be allowed to access without a password.
   * @returns {Promise<Object>} Response data returned by the server.
   * @memberof UserService
   */
    async updateInvitedUserNoPassword(token, email, noPasswordRequired) {
        const response = await axios.put(
            `${this.apiUrl}/user-invite-no-password`,
            {
                email,
                noPasswordRequired
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        )

        return response.data
    }
}